/* eslint-disable prettier/prettier */
import { MutationType } from '@tencent/slide-sdk-pb-model';
import { AddSectionMutation } from './section/add-section-mutation';
import { BindSectionToSlideMutation } from './section/bind-section-to-slide-mutation';
import { RemoveSectionMutation } from './section/remove-section-mutation';
import { SetSectionPropertiesMutation } from './section/set-section-properties-mutation';
import { AddShapeMutation, GroupMutation, RemoveShapeMutation, SetShapePropertiesMutation } from './shape';
import { MoveShapeMutation } from './shape/move-shape-mutation';
import { TransformShapeMutation } from './shape/transform-shape-mutation';
import { UngroupMutation } from './shape/ungroup-mutation';
import { AddAnimationMutation, MoveAnimationMutation, RemoveAnimationMutation, SetAnimationMutation } from './timing';
import { AddThemeMutation, RemoveThemeMutation, SetThemeMutation } from './theme';
import { SetTextDataMutation } from './text/set-text-data-mutation';
import {
    SetChartSpacePropertiesMutation,
    AddSubChartMutation,
    SetSubChartMutation,
    RemoveSubChartMutation,
    MoveSubChartMutation,
} from './shape/chart';
import {
    SetDiagramDataMutation,
    SetDiagramDrawingMutation,
    SetDiagramsMutation,
} from './shape/smartArt';
import {
    ValidMutationData,
    type IValidAddAnimationMutationData,
    type IValidAddPageMutationData,
    type IValidAddSectionMutationData,
    type IValidAddShapeMutationData,
    type IValidAddSubChartMutationData,
    type IValidBindSectionToSlideMutationData,
    type IValidGroupMutationData,
    type IValidMoveAnimationMutationData,
    type IValidMovePageMutationData,
    type IValidMoveShapeMutationData,
    type IValidMutationData,
    type IValidRemoveAnimationMutationData,
    type IValidRemovePageMutationData,
    type IValidRemoveSectionMutationData,
    type IValidRemoveShapeMutationData,
    type IValidSetAnimationPropertiesMutationData,
    type IValidSetPagePropertiesMutationData,
    type IValidSetSectionPropertiesMutationData,
    type IValidSetShapePropertiesMutationData,
    type IValidSetSlideSizeMutationData,
    type IValidSetSubChartPropertiesMutationData,
    type IValidTransformShapeMutationData,
    type IValidUngroupMutationData,
    type IValidSetTextDataMutationData,
    type IValidAddThemeMutationData,
    type IValidRemoveThemeMutationData,
    type IValidSetThemeMutationData,
    type IValidInsertTableRowMutationData,
    type IValidInsertTableColMutationData,
    type IValidDeleteTableColMutationData,
    type IValidDeleteTableRowMutationData,
    type IValidMergeTableCellsMutationData,
    type IValidUnmergeTableCellsMutationData,
    type IValidSetTableColWidthsMutationData,
    type IValidSetTableRowHeightsMutationData,
    type IValidSetTableCellPropertiesMutationData,
    type IValidSetNotesSizeMutationData,
    type IValidSetDiagramDataMutationData,
    type IValidSetDiagramDrawingMutationData,
    type IValidSetDiagramsMutationData
} from './base';
import { SetTextBodyPropertiesMutation } from './text/set-text-body-properties-mutation';
import { AddPageMutation } from './page/add-page-mutation';
import { MovePageMutation } from './page/move-page-mutation';
import { RemovePageMutation } from './page/remove-page-mutation';
import { SetPagePropertiesMutation } from './page/set-page-properties-mutation';
import { SetPresentationPropertiesMutation } from './presentation/set-presentation-properties-mutation';
import { SetSlideSizeMutation } from './presentation/set-slide-size-mutation';
import { DeleteTableColMutation } from './shape/table/delete-table-col-mutation';
import { DeleteTableRowMutation } from './shape/table/delete-table-row-mutation';
import { InsertTableColMutation } from './shape/table/insert-table-col-mutation';
import { InsertTableRowMutation } from './shape/table/insert-table-row-mutation';
import { MergeTableCellsMutation } from './shape/table/merge-table-cells-mutation';
import { SetTableColWidthsMutation } from './shape/table/set-table-col-width-mutation';
import { SetTableRowHeightsMutation } from './shape/table/set-table-row-height-mutation';
import { UnMergeTableCellsMutation } from './shape/table/unmerge-table-cells-mutation';
import { SetTableCellPropertiesMutation } from './shape/table/set-table-cell-properties-mutation';
import { EmptyMutation } from './base/empty-mutation';
import { SetNotesSizeMutation } from './presentation/set-notes-size-mutation';
import { UpdateResourceMapMutation } from './resource/update-resource-map-mutation';
import { ComputeGroupTransformMutation } from './shape/compute-group-transform-mutation';
import { AddCommentMutation } from './comment/add-comment-mutation';
import { RemoveCommentMutation } from './comment/remove-comment-mutation';
import { SetCommentPropertiesMutation } from './comment/set-comment-properties-mutation';
import { SetDataExtensionMutation } from './data-extension/set-data-extension-mutation';
import { RemoveDataExtensionMutation } from './data-extension/remove-data-extension-mutation';
import type { Mutation } from './base/base-mutation';
import type {
    IValidEmptyMutationData,
    IValidSetPresentationPropertiesMutationData,
    IValidSetTextBodyPropertiesMutationData,
    MaybeMutationData,
    IValidSetChartSpacePropertiesMutationData,
    IValidUpdateResourceMapMutationData,
    IValidRemoveSubChartMutationData,
    IValidComputeGroupTransformMutationData,
    IValidMoveSubChartMutationData,
    IValidAddCommentMutationData,
    IValidRemoveCommentMutationData,
    IValidSetCommentPropertiesMutationData,
    IValidSetDataExtensionMutationData,
    IValidRemoveDataExtensionMutationData,
} from './base';

const MutationFactoryMap = new Map<MutationType, (data: IValidMutationData) => Mutation>([
    [MutationType.Empty, (data: IValidMutationData) => new EmptyMutation(data as IValidEmptyMutationData)],
    [
        MutationType.SetPresentationProperties,
        (data: IValidMutationData) =>
            new SetPresentationPropertiesMutation(data as IValidSetPresentationPropertiesMutationData),
    ],
    [
        MutationType.SetSlideSize,
        (data: IValidMutationData) => new SetSlideSizeMutation(data as IValidSetSlideSizeMutationData),
    ],
    [MutationType.AddPage, (data: IValidMutationData) => new AddPageMutation(data as IValidAddPageMutationData)],
    [
        MutationType.RemovePage,
        (data: IValidMutationData) => new RemovePageMutation(data as IValidRemovePageMutationData),
    ],
    [MutationType.MovePage, (data: IValidMutationData) => new MovePageMutation(data as IValidMovePageMutationData)],
    [
        MutationType.SetPageProperties,
        (data: IValidMutationData) => new SetPagePropertiesMutation(data as IValidSetPagePropertiesMutationData),
    ],
    [MutationType.AddShape, (data: IValidMutationData) => new AddShapeMutation(data as IValidAddShapeMutationData)],
    [
        MutationType.RemoveShape,
        (data: IValidMutationData) => new RemoveShapeMutation(data as IValidRemoveShapeMutationData),
    ],
    [
        MutationType.TransformShape,
        (data: IValidMutationData) => new TransformShapeMutation(data as IValidTransformShapeMutationData),
    ],
    [MutationType.MoveShape, (data: IValidMutationData) => new MoveShapeMutation(data as IValidMoveShapeMutationData)],
    [MutationType.Group, (data: IValidMutationData) => new GroupMutation(data as IValidGroupMutationData)],
    [MutationType.Ungroup, (data: IValidMutationData) => new UngroupMutation(data as IValidUngroupMutationData)],
    [
        MutationType.SetShapeProperties,
        (data: IValidMutationData) => new SetShapePropertiesMutation(data as IValidSetShapePropertiesMutationData),
    ],
    [
        MutationType.AddSection,
        (data: IValidMutationData) => new AddSectionMutation(data as IValidAddSectionMutationData),
    ],
    [
        MutationType.RemoveSection,
        (data: IValidMutationData) => new RemoveSectionMutation(data as IValidRemoveSectionMutationData),
    ],
    [
        MutationType.SetSectionProperties,
        (data: IValidMutationData) => new SetSectionPropertiesMutation(data as IValidSetSectionPropertiesMutationData),
    ],
    [
        MutationType.BindSectionToSlide,
        (data: IValidMutationData) => new BindSectionToSlideMutation(data as IValidBindSectionToSlideMutationData),
    ],
    [
        MutationType.InsertTableRow,
        (data: IValidMutationData) => new InsertTableRowMutation(data as IValidInsertTableRowMutationData),
    ],
    [
        MutationType.InsertTableCol,
        (data: IValidMutationData) => new InsertTableColMutation(data as IValidInsertTableColMutationData),
    ],
    [
        MutationType.DeleteTableRow,
        (data: IValidMutationData) => new DeleteTableRowMutation(data as IValidDeleteTableRowMutationData),
    ],
    [
        MutationType.DeleteTableCol,
        (data: IValidMutationData) => new DeleteTableColMutation(data as IValidDeleteTableColMutationData),
    ],
    [
        MutationType.MergeTableCells,
        (data: IValidMutationData) => new MergeTableCellsMutation(data as IValidMergeTableCellsMutationData),
    ],
    [
        MutationType.UnmergeTableCells,
        (data: IValidMutationData) => new UnMergeTableCellsMutation(data as IValidUnmergeTableCellsMutationData),
    ],
    [
        MutationType.SetTableColWidths,
        (data: IValidMutationData) => new SetTableColWidthsMutation(data as IValidSetTableColWidthsMutationData),
    ],
    [
        MutationType.SetTableRowHeights,
        (data: IValidMutationData) => new SetTableRowHeightsMutation(data as IValidSetTableRowHeightsMutationData),
    ],
    [
        MutationType.SetTableCellProperties,
        (data: IValidMutationData) =>
            new SetTableCellPropertiesMutation(data as IValidSetTableCellPropertiesMutationData),
    ],
    [
        MutationType.AddAnimation,
        (data: IValidMutationData) => new AddAnimationMutation(data as IValidAddAnimationMutationData),
    ],
    [
        MutationType.RemoveAnimation,
        (data: IValidMutationData) => new RemoveAnimationMutation(data as IValidRemoveAnimationMutationData),
    ],
    [
        MutationType.SetAnimationProperties,
        (data: IValidMutationData) => new SetAnimationMutation(data as IValidSetAnimationPropertiesMutationData),
    ],
    [
        MutationType.MoveAnimation,
        (data: IValidMutationData) => new MoveAnimationMutation(data as IValidMoveAnimationMutationData),
    ],
    [
        MutationType.SetTextData,
        (data: IValidMutationData) => new SetTextDataMutation(data as IValidSetTextDataMutationData),
    ],
    [MutationType.AddTheme, (data: IValidMutationData) => new AddThemeMutation(data as IValidAddThemeMutationData)],
    [
        MutationType.RemoveTheme,
        (data: IValidMutationData) => new RemoveThemeMutation(data as IValidRemoveThemeMutationData),
    ],
    [MutationType.SetTheme, (data: IValidMutationData) => new SetThemeMutation(data as IValidSetThemeMutationData)],
    [
        MutationType.SetTextBodyProperties,
        (data: IValidMutationData) =>
            new SetTextBodyPropertiesMutation(data as IValidSetTextBodyPropertiesMutationData),
    ],
    [
        MutationType.SetChartSpaceProperties,
        (data: IValidMutationData) =>
            new SetChartSpacePropertiesMutation(data as IValidSetChartSpacePropertiesMutationData),
    ],
    [
        MutationType.AddSubChart,
        (data: IValidMutationData) => new AddSubChartMutation(data as IValidAddSubChartMutationData),
    ],
    [
        MutationType.RemoveSubChart,
        (data: IValidMutationData) => new RemoveSubChartMutation(data as IValidRemoveSubChartMutationData),
    ],
    [
        MutationType.MoveSubChart,
        (data: IValidMutationData) => new MoveSubChartMutation(data as IValidMoveSubChartMutationData),
    ],
    [
        MutationType.SetSubChartProperties,
        (data: IValidMutationData) => new SetSubChartMutation(data as IValidSetSubChartPropertiesMutationData),
    ],
    [
        MutationType.SetNotesSize,
        (data: IValidMutationData) => new SetNotesSizeMutation(data as IValidSetNotesSizeMutationData),
    ],
    [
        MutationType.UpdateResourceMap,
        (data: IValidMutationData) => new UpdateResourceMapMutation(data as IValidUpdateResourceMapMutationData),
    ],
    [
        MutationType.ComputeGroupTransform,
        (data: IValidMutationData) =>
            new ComputeGroupTransformMutation(data as IValidComputeGroupTransformMutationData),
    ],
    [
        MutationType.SetDiagramData,
        (data: IValidMutationData) =>
            new SetDiagramDataMutation(data as IValidSetDiagramDataMutationData),
    ],
    [
        MutationType.SetDiagramDrawing,
        (data: IValidMutationData) =>
            new SetDiagramDrawingMutation(data as IValidSetDiagramDrawingMutationData),
    ],
    [
        MutationType.SetDiagrams,
        (data: IValidMutationData) =>
            new SetDiagramsMutation(data as IValidSetDiagramsMutationData),
    ],
    [
        MutationType.AddComment,
        (data: IValidMutationData) =>
            new AddCommentMutation(data as IValidAddCommentMutationData),
    ],
    [
        MutationType.RemoveComment,
        (data: IValidMutationData) =>
            new RemoveCommentMutation(data as IValidRemoveCommentMutationData),
    ],
    [
        MutationType.SetCommentProperties,
        (data: IValidMutationData) =>
            new SetCommentPropertiesMutation(data as IValidSetCommentPropertiesMutationData),
    ],
    [
        MutationType.SetDataExtension,
        (data: IValidMutationData) =>
            new SetDataExtensionMutation(data as IValidSetDataExtensionMutationData),
    ],
    [
        MutationType.RemoveDataExtension,
        (data: IValidMutationData) =>
            new RemoveDataExtensionMutation(data as IValidRemoveDataExtensionMutationData),
    ]
    // TODO 注册其他Mutation
    // [
    //     MutationType.SetChartSpace,
    //     (data: IValidMutationData) => new SetChartSpacePropertiesMutation(data as IValidSetChartSpacePropertiesMutationData),
    // ],
]);

export function createMutations(mutationDataItems: MaybeMutationData[]): Mutation[] {
    return mutationDataItems.map((data, index) => {
        const validData = ValidMutationData(data);
        if (!validData?.type) {
            throw new Error(
                `Invalid mutation data @${index} [${MutationType[data.type ?? MutationType.Empty]}]: ${JSON.stringify(
                    data,
                )}`,
            );
        }

        const factory = MutationFactoryMap.get(validData.type);
        if (!factory) {
            throw new Error(`mutation factory is not registered: ${MutationType[validData.type]}`);
        }

        return factory(validData);
    });
}
